/**
 * ImportModal Component
 *
 * Multi-stage import modal with file upload and validation workflow.
 * Supports three stages:
 * 1. Select Import Source (radio buttons)
 * 2. Upload File (drag-and-drop)
 * 3. Validate & Preview (optional, based on action config)
 *
 * @module components/import
 */

import { Button } from '@praetorian-chariot/ui';
import React, { useState } from 'react';

import { Modal } from '@/components/Modal';

import {
  FileUploadStage,
  ImportSourceSelector,
  ProgressIndicator,
  ValidationStage,
} from './stages';
import { ImportAction, ImportModalState, ImportStage } from './types';

/**
 * Props for ImportModal component
 */
export interface ImportModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Available import source actions */
  actions: ImportAction[];
  /** Callback when file is selected - should upload to S3 and return path */
  onFileSelected?: (action: ImportAction, file: File) => Promise<string>;
  /** Callback when import is confirmed with action, file, and S3 path */
  onImport?: (
    selectedAction: ImportAction,
    file: File,
    filePath: string
  ) => void;
  /** Selected configuration values (for config options like auto_approve) */
  selectedConfigs?: Record<string, boolean | string>;
  /** Callback when a configuration option changes */
  onConfigChange?: (key: string, value: boolean | string) => void;
}

/**
 * ImportModal Component
 *
 * Generic multi-stage modal for importing data from various sources.
 * Provides file upload, validation, and preview capabilities.
 *
 * @param props - Component props
 * @returns Import modal JSX
 *
 * @example
 * ```tsx
 * const importActions: ImportAction[] = [
 *   {
 *     label: 'Qualys',
 *     value: 'qualys',
 *     validation: {
 *       enabled: true,
 *       previewRows: 10,
 *       validate: validateCSV,
 *     },
 *   },
 * ];
 *
 * <ImportModal
 *   open={showModal}
 *   onClose={() => setShowModal(false)}
 *   title="Import Vulnerabilities"
 *   actions={importActions}
 *   onImport={(action, file) => handleImport(action, file)}
 * />
 * ```
 */
export function ImportModal({
  open,
  onClose,
  title,
  actions,
  onFileSelected,
  onImport,
  selectedConfigs,
  onConfigChange,
}: ImportModalProps) {
  // Multi-stage state management
  const [state, setState] = useState<ImportModalState>({
    currentStage: ImportStage.SELECT_SOURCE,
    selectedAction: null,
    uploadedFile: null,
    uploadedFilePath: null,
    validationResult: null,
    isValidating: false,
    isUploading: false,
  });

  // State transition helpers
  const goToStage = (stage: ImportStage) => {
    setState(prev => ({ ...prev, currentStage: stage }));
  };

  const handleSourceSelected = (action: ImportAction) => {
    setState(prev => ({
      ...prev,
      selectedAction: action,
      // Don't transition stage here - let "Next" button handle it
    }));

    // Initialize config defaults when action is selected
    if (action.configs && onConfigChange) {
      action.configs.forEach(config => {
        onConfigChange(config.key, config.defaultValue);
      });
    }
  };

  const handleFileUploaded = async (file: File | null) => {
    if (!file) {
      // Clear file state
      setState(prev => ({
        ...prev,
        uploadedFile: null,
        uploadedFilePath: null,
      }));
      return;
    }

    // Set the file and start uploading
    setState(prev => ({
      ...prev,
      uploadedFile: file,
      isUploading: true,
    }));

    try {
      // Upload file to S3 if callback provided
      if (onFileSelected && state.selectedAction) {
        const filePath = await onFileSelected(state.selectedAction, file);
        setState(prev => ({
          ...prev,
          uploadedFilePath: filePath,
          isUploading: false,
        }));
      } else {
        setState(prev => ({ ...prev, isUploading: false }));
      }
    } catch (error) {
      console.error('File upload failed:', error);
      setState(prev => ({
        ...prev,
        uploadedFile: null,
        uploadedFilePath: null,
        isUploading: false,
      }));
      throw error; // Re-throw to show error in UI
    }
  };

  const handleValidateFile = async () => {
    if (!state.uploadedFile || !state.selectedAction) return;

    // Transition to validation stage
    setState(prev => ({
      ...prev,
      currentStage: ImportStage.VALIDATE,
      isValidating: true,
    }));

    // Perform validation
    if (state.selectedAction.validation?.validate) {
      const result = await state.selectedAction.validation.validate(
        state.uploadedFile
      );
      setState(prev => ({
        ...prev,
        validationResult: result,
        isValidating: false,
      }));
    } else {
      // No validator provided, mark as valid
      setState(prev => ({
        ...prev,
        validationResult: { valid: true },
        isValidating: false,
      }));
    }
  };

  const handleImport = () => {
    if (!state.selectedAction || !state.uploadedFile || !state.uploadedFilePath)
      return;

    if (onImport) {
      onImport(
        state.selectedAction,
        state.uploadedFile,
        state.uploadedFilePath
      );
    }

    // Close modal and reset state
    handleClose();
  };

  const handleClose = () => {
    setState({
      currentStage: ImportStage.SELECT_SOURCE,
      selectedAction: null,
      uploadedFile: null,
      uploadedFilePath: null,
      validationResult: null,
      isValidating: false,
      isUploading: false,
    });
    onClose();
  };

  // Stage rendering logic
  const renderStage = () => {
    switch (state.currentStage) {
      case ImportStage.SELECT_SOURCE:
        return (
          <ImportSourceSelector
            actions={actions}
            selectedAction={state.selectedAction?.value || null}
            onSelect={handleSourceSelected}
          />
        );

      case ImportStage.UPLOAD_FILE:
        if (!state.selectedAction) return null;
        return (
          <FileUploadStage
            selectedAction={state.selectedAction}
            uploadedFile={state.uploadedFile}
            onFileUpload={handleFileUploaded}
            onBack={() => goToStage(ImportStage.SELECT_SOURCE)}
            selectedConfigs={selectedConfigs}
            onConfigChange={onConfigChange}
          />
        );

      case ImportStage.VALIDATE:
        if (!state.selectedAction || !state.uploadedFile) return null;
        return (
          <ValidationStage
            selectedAction={state.selectedAction}
            uploadedFile={state.uploadedFile}
            validationResult={state.validationResult}
            isValidating={state.isValidating}
            onBack={() => goToStage(ImportStage.UPLOAD_FILE)}
            onImport={handleImport}
          />
        );

      default:
        return null;
    }
  };

  // Helper to get back button action based on current stage
  const getBackButtonAction = () => {
    switch (state.currentStage) {
      case ImportStage.UPLOAD_FILE:
        return () => goToStage(ImportStage.SELECT_SOURCE);
      case ImportStage.VALIDATE:
        return () => goToStage(ImportStage.UPLOAD_FILE);
      default:
        return undefined;
    }
  };

  // Get next button config based on stage
  const getNextButtonConfig = () => {
    switch (state.currentStage) {
      case ImportStage.SELECT_SOURCE:
        return {
          label: 'Next',
          disabled: !state.selectedAction,
          onClick: () => {
            if (state.selectedAction) {
              goToStage(ImportStage.UPLOAD_FILE);
            }
          },
        };

      case ImportStage.UPLOAD_FILE:
        return {
          label: state.selectedAction?.validation?.enabled ? 'Next' : 'Import',
          disabled:
            !state.uploadedFile || state.isUploading || !state.uploadedFilePath,
          onClick: async () => {
            if (!state.uploadedFile || !state.uploadedFilePath) return;

            if (state.selectedAction?.validation?.enabled) {
              // Move to validation stage and validate the file
              await handleValidateFile();
            } else {
              // No validation needed, proceed directly to import
              handleImport();
            }
          },
        };

      case ImportStage.VALIDATE:
        return {
          label: 'Import',
          disabled:
            state.isValidating ||
            !state.validationResult?.valid ||
            !state.uploadedFile ||
            !state.uploadedFilePath,
          onClick: handleImport,
        };

      default:
        return {
          label: 'Next',
          disabled: true,
          onClick: () => {},
        };
    }
  };

  const backAction = getBackButtonAction();
  const isFirstStage = state.currentStage === ImportStage.SELECT_SOURCE;
  const nextConfig = getNextButtonConfig();

  return (
    <Modal
      title={title}
      open={open}
      onClose={handleClose}
      size="xl"
      closeOnOutsideClick={false}
      hideFooter={true}
    >
      <div className="space-y-4">
        {/* Progress indicator */}
        <ProgressIndicator
          currentStage={state.currentStage}
          showValidation={state.selectedAction?.validation?.enabled || false}
        />

        {/* Stage content */}
        {renderStage()}

        {/* Custom Footer */}
        <div className="border-chariot-stroke flex justify-between border-t py-4">
          <Button label="Cancel" styleType="secondary" onClick={handleClose} />
          <div className="flex gap-2">
            <Button
              label="Back"
              styleType="primary"
              disabled={isFirstStage}
              onClick={backAction || (() => {})}
            />
            <Button
              label={nextConfig.label}
              styleType="primary"
              disabled={nextConfig.disabled}
              onClick={nextConfig.onClick}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}

// Re-export types for convenience
export type { ImportAction } from './types';
